import { HttpClient } from "@angular/common/http";
import {
  Component,
  effect,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { ICON_TYPES } from "../../../constants/iconTypes";

@Component({
  standalone: true,
  selector: "app-svg-icon",
  imports: [],
  template: "",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./svg-icon.component.scss",
})
export class SvgIconComponent implements OnInit, OnDestroy, OnChanges {
  public iconName = input<string>();
  public iconSize = input<string>("sm");
  public iconColor = input<string>("black");
  private previousColor = this.iconColor();
  private svgElement: HTMLElement | undefined;
  private http = inject(HttpClient);
  private elementRef = inject(ElementRef);
  private renderer = inject(Renderer2);
  private svgSubscription: Subscription | undefined;

  constructor() {
    effect(() => {
      const iconName = this.iconName();
      const iconColor = this.iconColor();
      if (iconName && iconColor) {
        const previousColorClass = this.previousColor + "-" + ICON_TYPES.get(iconName);
        const newColorClass = iconColor + "-" + ICON_TYPES.get(iconName);

        this.svgElement?.classList.remove(previousColorClass);
        this.svgElement?.classList.add(newColorClass);
      }
    });
  }

  public ngOnInit(): void {
    this.loadIcon();
  }
  public ngOnChanges(changes: SimpleChanges) {
    const iconName = this.iconName();
    if (iconName && changes?.["iconColor"]) {
      const previousColorClass = changes?.["iconColor"].previousValue + "-" + ICON_TYPES.get(iconName);
      const newColorClass = changes?.["iconColor"].currentValue + "-" + ICON_TYPES.get(iconName);

      this.svgElement?.classList.remove(previousColorClass);
      this.svgElement?.classList.add(newColorClass);
    }
  }
  public ngOnDestroy(): void {
    if (this.svgSubscription) {
      this.svgSubscription.unsubscribe();
    }
  }

  /**
   * Loads an SVG icon based on the `iconName` input and appends it to the component's host element.
   * The function constructs the icon's path and fetches the SVG content using an HTTP GET request.
   * If a previous subscription exists, it unsubscribes to prevent memory leaks.
   * Upon successful retrieval of SVG content, it clears any existing SVG elements from the host,
   * applies styles, and appends the new SVG to the host element. In case of an error, logs the error message.
   */
  private loadIcon(): void {
    const iconPath = `assets/icons/${this.iconName()}.svg`;

    if (this.svgSubscription) {
      this.svgSubscription.unsubscribe();
    }

    this.svgSubscription = this.http.get(iconPath, { responseType: "text" }).subscribe({
      next: (svgText) => {
        // Clear any existing content
        const nativeElement = this.elementRef.nativeElement;
        while (nativeElement.firstChild) {
          nativeElement.removeChild(nativeElement.firstChild);
        }

        // Create a temporary container for the SVG content
        const svgContainer = this.renderer.createElement("div");
        svgContainer.innerHTML = svgText;
        this.svgElement = svgContainer.querySelector("svg");
        if (this.svgElement) {
          // Apply styles to the SVG element
          this.applyIconStyles(this.svgElement);

          // Append the SVG element to the host element
          this.renderer.appendChild(this.elementRef.nativeElement, this.svgElement);
        }
      },
      error: (error) => {
        console.error(`Error loading SVG: ${iconPath}`, error);
      },
    });
  }

  /**
   * Apply styles to the given SVG element.
   *
   * @param svgElement the SVG element to apply styles to
   *
   * This method sets the width and height of the SVG element to the
   * `iconSize` property. If `iconName` is set, it adds a class to the
   * SVG element for the given icon type. The class name is of the form
   * `<iconColor>-<iconType>`.
   */
  private applyIconStyles(svgElement: HTMLElement): void {
    const iconName = this.iconName();
    const iconColor = this.iconColor();
    const iconSize = this.iconCssVars.get(this.iconSize());
    if (iconName && iconColor && iconSize) {
      this.renderer.setStyle(svgElement, "width", iconSize);
      this.renderer.setStyle(svgElement, "height", iconSize);
      svgElement.classList.add(iconColor + "-" + ICON_TYPES.get(iconName));
    }
  }

  private iconCssVars = new Map<string, string>([
    ["xs", "var(--p-icon-size-xs)"],
    ["sm", "var(--p-icon-size-sm)"],
    ["m", "var(--p-icon-size-m)"],
    ["l", "var(--p-icon-size-l)"],
    ["xl", "var(--p-icon-size-xl)"],
    ["xxl", "var(--p-icon-size-xxl)"],
  ]);
}
